import _ from 'lodash'
import React from 'react'
import { connect } from 'react-redux'

import {
  getQuestionUsage,
  getQuestionUsageRequest,
} from 'mednet-cns/src/reducers/questionUpdate'

import { Subtitle1, Subtitle2, Subtitle3 } from 'pharmacy/src/typography'
import { Icon } from 'pharmacy/src/display/icon'
import { IconWithPopup } from 'pharmacy/src/display/iconWithPopup'

import * as chipCss from './lastUsedInfo.scss'
import * as css from './questionUsageLine.scss'

// Second line under an update's chip: whether ANOTHER item on the same question
// (a different answer, a poll, or the question approval) has already gone out —
// useful when this specific item hasn't, but the question has been covered.
//
// The card's own item is matched by (contentId, type): an edited answer's several
// revisions share a content_id, so they count as one item. If the question's only
// usage is the card's own item, there is no "another" item and the line is hidden.
// When other items exist, the own item's rows are still listed but labeled "this ..."
// so the moderator can tell them apart.
//
// This component only reads usage; the page batches one fetchQuestionUsage for all
// visible questions at once (like the main chip's usage), so we don't fire a request
// per card. That fetch may resolve before the main chip; we still gate DISPLAY on the
// main chip below so results never show ahead of it.
//
// Loading/error is deliberate so absence is never misread as "not sent":
//   - `hidden` (parent's prerequisite not ready) -> render nothing. The parent hides
//     this line while the update-usage fetch that drives the main chip is still loading
//     (no second loader) or if it failed (the primary check is what matters).
//   - question usage not in yet -> a small "checking other items" loader
//   - question-usage fetch errored -> an explicit "couldn't check" notice, never blank
//   - loaded, no OTHER digest usage -> nothing (a confirmed, safe blank)

// Tag one channel's sends (digests or newsletters) with whether each is the card's
// own item (matched by content id + type), newest first. The two channels stay
// separate — each renders under its own popup heading.
const prepareChannel = (entries, ownContentId, ownType) =>
  _.orderBy(
    (entries || []).map((entry) => ({
      ...entry,
      isOwn:
        entry.type === ownType &&
        Number(entry.contentId) === Number(ownContentId),
    })),
    ['date'],
    ['desc']
  )

// One line inside the popup's channel list. Just the item type — the channel is the
// section header. "this" marks the card's own item when other items are also present.
const popupRowLabel = (row) => `${row.isOwn ? 'this ' : ''}${row.type}`

class QuestionUsageLine extends React.Component {
  // One channel's section inside the popup (not the on-card line itself): a heading
  // plus a dated row per send.
  renderPopupChannel(rows, heading) {
    if (!rows.length) {
      return null
    }
    return (
      <div>
        <Subtitle1>{heading}</Subtitle1>
        {rows.map((row, index) => (
          <Subtitle2 key={index} className={chipCss.row}>
            {row.date} — {popupRowLabel(row)}
          </Subtitle2>
        ))}
      </div>
    )
  }

  render() {
    const { hidden, usage, isError, ownContentId, ownType } = this.props

    // The parent hides this secondary line until its prerequisite (the update-usage
    // fetch that drives the main chip) is ready, so the two never show two loaders
    // and the line is dropped if that primary check failed.
    if (hidden) {
      return null
    }

    // The error/loading notices only fire while THIS question has no usage record
    // yet. The request flag is shared across every question-usage batch on the
    // page, so guarding on `!usage` keeps a later, unrelated batch that errors or
    // is in flight from blanking (or re-spinnering) a question a prior batch
    // already loaded — the same trust-the-record rule the main chip uses.
    if (!usage) {
      // A failed fetch and nothing to show: say the check failed rather than leave
      // a blank that reads as "not sent" — a stale/misleading line would be worse.
      if (isError) {
        return (
          <Subtitle3 className={css.error}>
            Couldn’t check other items from this question
          </Subtitle3>
        )
      }

      // No record yet and no error: this question's batch hasn't returned, so show
      // the loader. The receive reducer seeds every requested question with an empty
      // record, so a genuinely-"no usage" question is defined-but-empty and never
      // reaches here — meaning !usage reliably means "still pending", and we don't
      // need the page-shared isLoading/isLoaded flag (which flips loaded when the
      // FIRST overlapping batch returns and would blank a still-pending question).
      return (
        <Subtitle3 className={css.loading}>
          <Icon icon={['far', 'spinner']} size="small" /> Checking other items
          from this question…
        </Subtitle3>
      )
    }

    const digestRows = prepareChannel(usage.digests, ownContentId, ownType)
    const newsletterRows = prepareChannel(
      usage.newsletters,
      ownContentId,
      ownType
    )

    // Like the main chip, the line is driven by DIGEST usage: show it only when a
    // qualifying item on the question went out in a digest. Newsletters are extra
    // context in the popup, not a trigger. A confirmed "no usage" is blank.
    //
    // With an own item (search-panel card, one item per card) we exclude it and phrase
    // it "Another item …". Without one (digest card, one card per whole question) there
    // is nothing to exclude, so every digest row counts and it reads "An item …".
    const isItemLevel = Boolean(ownType)
    const qualifyingDigestRows = isItemLevel
      ? digestRows.filter((row) => !row.isOwn)
      : digestRows
    if (!qualifyingDigestRows.length) {
      return null
    }

    const latest = qualifyingDigestRows[0]
    const summary = isItemLevel
      ? `Another item from this question was sent in a digest on ${latest.date}`
      : `An item from this question was sent in a digest on ${latest.date}`

    return (
      <IconWithPopup
        text={summary}
        className={css.line}
        iconProps={{ size: 'sm' }}
      >
        <div>
          {this.renderPopupChannel(
            digestRows,
            'Digests with items from this question'
          )}
          {this.renderPopupChannel(
            newsletterRows,
            'Newsletters with items from this question'
          )}
        </div>
      </IconWithPopup>
    )
  }
}

const mapStateToProps = (state, ownProps) => {
  const { specialtyId, questionId } = ownProps
  const request = getQuestionUsageRequest(state)
  return {
    usage: getQuestionUsage(state, specialtyId, questionId),
    isError: request.isError,
  }
}

export default connect(mapStateToProps)(QuestionUsageLine)
